/**
 * Chart color tokens.
 *
 * Recharts needs concrete color strings passed as props, so the data-viz
 * palette lives here (in OKLCH, per the design system) rather than as Tailwind
 * classes. These mirror the brand anchors documented in DESIGN.md > Colors;
 * keep them in sync with the brand palette in tailwind.config.ts.
 *
 * OKLCH renders in SVG fill/stroke on every browser this portal targets.
 */

import { brandSwatch, type BrandSwatch } from "@/utils/posStores";

export const chartColors = {
  brandRed: "oklch(0.5055 0.1715 24.82)", // #b22b2e
  brandGreen: "oklch(0.3454 0.0646 176.00)", // #004438
  brandYellow: "oklch(0.8833 0.1654 98.48)", // #f5d93e
  brandGray: "oklch(0.4638 0.0035 264.53)", // #58595b
  /** Cartesian grid lines — a faint warm gray. */
  grid: "oklch(0.9285 0.0027 286.35)",
} as const;

/**
 * Customer value ladder, low to high frequency:
 * gray -> muted teal -> deeper teal -> brand green -> brand red (superfan).
 */
export const LADDER_COLORS: readonly string[] = [
  chartColors.brandGray,
  "oklch(0.6593 0.0381 176.06)",
  "oklch(0.5442 0.0515 174.84)",
  chartColors.brandGreen,
  chartColors.brandRed,
];

/**
 * Recency segments, freshest to most-lapsed:
 * brand green -> lime -> brand yellow -> orange -> brand red.
 */
export const RECENCY_COLORS: readonly string[] = [
  chartColors.brandGreen,
  "oklch(0.7283 0.1454 118.26)",
  chartColors.brandYellow,
  "oklch(0.7224 0.1378 64.61)",
  chartColors.brandRed,
];

/**
 * Categorical series palette for multi-line/-bar charts (e.g. one line per store
 * in the orderSpy History comparison). Ordered for maximum adjacent contrast and
 * anchored on the brand hues; consumers cycle it with `% length` for more series
 * than colors.
 */
export const SERIES_COLORS: readonly string[] = [
  chartColors.brandRed,
  chartColors.brandGreen,
  "oklch(0.7224 0.1378 64.61)", // orange
  "oklch(0.5442 0.0515 174.84)", // deep teal
  chartColors.brandYellow,
  chartColors.brandGray,
  "oklch(0.7283 0.1454 118.26)", // lime
  "oklch(0.6593 0.0381 176.06)", // muted teal
];

/**
 * orderSpy per-brand swatches (mirrors `--brand-swatch-1..8` in src/index.css;
 * Recharts needs a JS color string, so this is the source of truth for the
 * chart and `posStores.ts` is the source of truth for the Tailwind classes).
 */
export const BRAND_SWATCH_COLORS: Record<BrandSwatch, string> = {
  1: "oklch(0.3454 0.0646 176.00)", // Mountain Mike's Pizza (brand green)
  2: "oklch(0.50 0.13 60.00)", // TandooriPizza (saffron)
  3: "oklch(0.48 0.14 330.00)", // PizzaMyHeart (plum)
  4: "oklch(0.4638 0.0035 264.53)", // fallback / unknown brand (brand gray)
  5: "oklch(0.50 0.13 220.00)", // hashed slot: blue
  6: "oklch(0.48 0.14 290.00)", // hashed slot: violet
  7: "oklch(0.48 0.13 140.00)", // hashed slot: green
  8: "oklch(0.50 0.12 200.00)", // hashed slot: cyan
};

const OKLCH_PATTERN = /^oklch\(([\d.]+)\s+([\d.]+)\s+([\d.]+)\)$/;

// Within one brand's pinned lines, step lightness (clamped to stay legible on
// the warm paper ground) and dash pattern so same-brand series stay
// distinguishable; cycles every 4 for a 5th+ pinned store of the same brand.
const LIGHTNESS_STEPS = [0, 0.12, -0.08, 0.2] as const;
const DASH_STEPS: readonly (string | undefined)[] = [undefined, "6 3", "2 2", "8 3 2 3"];

/**
 * Chart line style for the `indexWithinBrand`-th pinned store of `brand`
 * (0-indexed). Colour always reads as the brand's hue; lightness + dash
 * disambiguate multiple pins of the same brand.
 */
export function brandSeriesStyle(
  brand: string,
  indexWithinBrand: number,
): { stroke: string; strokeDasharray: string | undefined } {
  const base = BRAND_SWATCH_COLORS[brandSwatch(brand)];
  const match = OKLCH_PATTERN.exec(base);
  if (!match) return { stroke: base, strokeDasharray: undefined };
  const [, lRaw, c, h] = match;
  const step = ((indexWithinBrand % LIGHTNESS_STEPS.length) + LIGHTNESS_STEPS.length) % LIGHTNESS_STEPS.length;
  const l = Math.min(0.75, Math.max(0.3, Number(lRaw) + LIGHTNESS_STEPS[step]));
  return {
    stroke: `oklch(${l.toFixed(4)} ${c} ${h})`,
    strokeDasharray: DASH_STEPS[step],
  };
}
